import type { HeadUpdateResult, HeadsResult, SyncState } from './types'
import { EngineException } from './types'
import type { StoreBackend, SyncBackend } from './storeBackends'
import { TransferOrchestrator } from './transferOrchestrator'
import { sdkConsole } from './logger'

// ── Interfaces ─────────────────────────────────────────────────

export interface BlobTransaction {
    put(hash: string, data: Uint8Array): void
    /**
     * `referencedObjects` is HEAD metadata for server-side GC liveness. It is
     * deliberately not a client publish prerequisite list.
     */
    commit(headUpdate?: { base: string[]; new: string; referencedObjects?: string[] }): Promise<HeadUpdateResult>
}

export interface BlobRepository {
    // Data Access
    load(hash: string): Promise<Uint8Array | null>
    loadBatch(hashes: string[]): Promise<Map<string, Uint8Array>>
    /**
     * Load a demand-critical batch. Unlike loadBatch(), transient remote
     * transport failures reject so sqlite3mt page faults can retry at the
     * transport layer instead of misclassifying "temporarily unavailable" as
     * "CAS object confirmed missing".
     */
    loadBatchStrict?(hashes: string[]): Promise<Map<string, Uint8Array>>
    /** Check only local CAS/outbox state. Used by write-path dedup to avoid remote downloads. */
    hasObjectLocal(hash: string): Promise<boolean>
    /** Return remote-missing hashes without loading object bytes. Required for publish barriers. */
    findMissingObjects(hashes: Iterable<string>): Promise<string[]>
    prefetch(hashes: string[]): void

    // Mutation
    transaction(blobId: string): BlobTransaction

    // Sync
    getHeads(blobId: string, lane?: string): Promise<HeadsResult>
    updateHead(blobId: string, base: string[], newHead: string, referencedObjects?: string[], prefetchHints?: string[], lane?: string): Promise<HeadUpdateResult>
    push(blobId: string, opts?: {
        signal?: AbortSignal
        onProgress?: (uploaded: number, total: number, bytesUploaded: number, bytesTotal: number) => void
    }): Promise<string[]>
    /** Clear outbox entries after object upload succeeds (§7.2.3) */
    confirmPush(blobId: string, hashes: string[]): Promise<void>
    /** Drop local outbox entries that are known to belong to an abandoned stale local head. */
    discardOutbox?(blobId: string): Promise<number>
    /**
     * R2: Bulk push pre-collected objects directly to remote.
     * Recovery callers can force upload to ignore stale remote-visibility
     * assumptions after a server-side wipe or stale-head repair.
     */
    pushObjects(objects: Array<{ hash: string; data: Uint8Array }>, opts?: { force?: boolean }): Promise<void>
    /**
     * Ensure newly uploaded publish prerequisites are readable from the remote
     * CAS before publishing a head. Do not pass the full HEAD referencedObjects
     * live set here; those references are GC metadata, not a visibility barrier.
     */
    ensureRemoteVisible(hashes: Iterable<string>): Promise<void>

    // Persistence & Head Management
    getLocalHeads(blobId: string): Promise<string[]>
    setLocalHeads(blobId: string, heads: string[]): Promise<void>
    getRemoteHead(blobId: string): Promise<string | undefined>
    setRemoteHead(blobId: string, head: string): Promise<void>
    /** T1: Atomic local+remote head update in a single IDB transaction */
    setHeadsAtomically(blobId: string, opts: { local?: string[]; remote?: string }): Promise<void>
    /** Signal server-side soft-delete for GC lifecycle (§13) */
    softDeleteBlob(blobId: string): Promise<void>

    // Lease API (§94 §7) — resource-scoped
    /** Acquire a lease for a resource within a blob (Mode 0=exclusive, 1=sequential) */
    acquireLease(blobId: string, resourceId: string, mode: 0 | 1 | 2, writerKid: string, ttlSeconds?: number): Promise<LeaseResult | null>
    /** Refresh an existing lease TTL */
    refreshLease(blobId: string, leaseId: string, ttlSeconds?: number): Promise<LeaseResult | null>
    /** Release a lease */
    releaseLease(blobId: string, leaseId: string): Promise<boolean>
    /** Update head with optional lease fencing + atomic release (convenience) */
    updateHeadWithLease(blobId: string, base: string[], newHead: string, opts?: {
        leaseId?: string
        releaseLeaseId?: string
        referencedObjects?: string[]
        prefetchHints?: string[]
    }): Promise<HeadUpdateResult>
}

export interface LeaseResult {
    leaseId: string
    resourceId: string
    mode: number
    expiresAt: string
}

function isValidObjectHash(hash: string | null | undefined): hash is string {
    return typeof hash === 'string' && hash.length > 0
}

function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
}

type RemoteObjectLoadResult = {
    data: Uint8Array | null
    confirmedMissing: boolean
}

type WorkerGlobalScopeConstructor = abstract new (...args: never[]) => object

function isBrowserLikeObjectLoadRuntime(): boolean {
    if (typeof window !== 'undefined') {
        return true
    }
    const globalScope = globalThis as typeof globalThis & {
        WorkerGlobalScope?: WorkerGlobalScopeConstructor
        self?: object
    }
    const workerGlobalScope = globalScope.WorkerGlobalScope
    return typeof workerGlobalScope === 'function'
        && globalScope.self != null
        && globalScope.self instanceof workerGlobalScope
}

let blobRepositoryOpSeq = 0
// Local CAS/manifest cache reads are an accelerator, not a correctness gate.
// Under multi-worker browser stress, letting IDB stall each object load for
// hundreds of milliseconds burns the bounded-convergence budget, so fail over
// to the remote immutable-object path when local is genuinely slow. The 50 ms
// threshold catches the vast majority of local hits while still avoiding long
// stalls on cold or contended IDB.
const LOCAL_CACHE_LOAD_MAX_WAIT_MS = 50
// Immutable object reads are safe to duplicate, but the duplicate escape hatch
// must sit beyond the normal HTTP retry budget. Otherwise a slow-but-healthy
// remote CAS read creates parallel batch fallbacks and amplifies load. If an
// earlier direct load is genuinely stranded inside a browser runtime, later
// callers still get a bounded way out.
const INFLIGHT_LOAD_STALE_AFTER_MS = 95_000
// Response-time upload visibility is an optimization, not a permanent remote
// object oracle. Long-lived tabs may survive server GC cycles, so skip
// redundant uploads only while the assumption is fresh.
const REMOTE_VISIBLE_ASSUMPTION_TTL_MS = 24 * 60 * 60 * 1000
// Missing immutable objects are worth remembering briefly to dampen tight
// sqlite3mt retry loops, but not long enough to mask newly-uploaded content.
const REMOTE_MISSING_ASSUMPTION_TTL_MS = 5_000
// ── Implementation ─────────────────────────────────────────────

export class BlobRepositoryImpl implements BlobRepository {
    private orchestrator: TransferOrchestrator

    /** In-memory outbox fallback when IDB outbox is unavailable. */
    private memoryOutbox = new Map<string, Map<string, Uint8Array>>()

    /** Track objects uploaded in the current push cycle to avoid re-upload on retry. */
    private uploadedHashes = new Map<string, Set<string>>()

    /** Join concurrent direct-object loads for the same hash inside one runtime. */
    private inflightLoads = new Map<string, {
        promise: Promise<Uint8Array | null>
        startedAt: number
    }>()

    /** Hashes recently proven/acknowledged visible in the remote CAS. */
    private remoteVisibleHashes = new Map<string, number>()

    /** Hashes recently confirmed absent from the remote CAS. */
    private remoteMissingHashes = new Map<string, number>()

    /** Hashes uploaded in this runtime but not yet proven readable remotely. */
    private pendingRemoteVisibilityHashes = new Set<string>()

    constructor(
        private local: StoreBackend,
        private remote: StoreBackend & SyncBackend
    ) {
        this.orchestrator = new TransferOrchestrator(remote)
    }

    getDebugState(): {
        inflightLoads: Array<{ hash: string; ageMs: number }>
        remoteVisibleHashes: string[]
        remoteMissingHashes: string[]
        pendingRemoteVisibilityHashes: string[]
        local: unknown
        remote: unknown
    } {
        const now = Date.now()
        return {
            inflightLoads: Array.from(this.inflightLoads.entries()).map(([hash, load]) => ({
                hash,
                ageMs: now - load.startedAt,
            })),
            remoteVisibleHashes: Array.from(this.remoteVisibleHashes.keys()).filter(hash =>
                this.isRemoteVisibilityFresh(hash, now)
            ),
            remoteMissingHashes: Array.from(this.remoteMissingHashes.keys()).filter(hash =>
                this.isRemoteMissingFresh(hash, now)
            ),
            pendingRemoteVisibilityHashes: Array.from(this.pendingRemoteVisibilityHashes.values()),
            local: (this.local as { getDebugState?: () => unknown }).getDebugState?.() ?? null,
            remote: (this.remote as { getDebugState?: () => unknown }).getDebugState?.() ?? null,
        }
    }

    private persistLocalCacheBestEffort(hash: string, data: Uint8Array): void {
        void Promise.resolve(this.local.store(hash, data)).catch(error => {
            sdkConsole.warn('[sqlite3mt:repo] cache-local:error', { hash, error })
        })
    }

    private persistLocalBatchBestEffort(entries: Array<{ hash: string; data: Uint8Array }>): void {
        void Promise.resolve(this.local.storeBatch(entries)).catch(error => {
            sdkConsole.warn('[sqlite3mt:repo] cache-local-batch:error', {
                count: entries.length,
                hashes: entries.map(entry => entry.hash),
                error,
            })
        })
    }

    private async findMissingRemoteObjects(hashes: Iterable<string>): Promise<string[]> {
        const normalized = Array.from(new Set(Array.from(hashes).filter(isValidObjectHash)))
        if (normalized.length === 0) return []
        if (typeof this.remote.findMissingObjects !== 'function') {
            throw new Error('Remote store does not support POST /objects/missing required by publish visibility barriers.')
        }
        return await this.remote.findMissingObjects(normalized)
    }

    private isRemoteVisibilityFresh(hash: string, now = Date.now()): boolean {
        const visibleAt = this.remoteVisibleHashes.get(hash)
        if (visibleAt == null) {
            return false
        }
        if (now - visibleAt <= REMOTE_VISIBLE_ASSUMPTION_TTL_MS) {
            return true
        }
        this.remoteVisibleHashes.delete(hash)
        return false
    }

    private isRemoteMissingFresh(hash: string, now = Date.now()): boolean {
        const missingAt = this.remoteMissingHashes.get(hash)
        if (missingAt == null) {
            return false
        }
        if (now - missingAt <= REMOTE_MISSING_ASSUMPTION_TTL_MS) {
            return true
        }
        this.remoteMissingHashes.delete(hash)
        return false
    }

    private markRemoteVisible(hash: string, now = Date.now()): void {
        this.remoteMissingHashes.delete(hash)
        this.pendingRemoteVisibilityHashes.delete(hash)
        this.remoteVisibleHashes.set(hash, now)
    }

    private markRemoteMissing(hash: string, now = Date.now()): void {
        if (!this.isRemoteVisibilityFresh(hash, now)) {
            this.remoteMissingHashes.set(hash, now)
        }
    }

    private async loadLocalObjectForVisibilityRepair(hash: string): Promise<Uint8Array | null> {
        const outboxData = this.checkMemoryOutbox(hash)
        if (outboxData != null) {
            return outboxData
        }
        return this.loadLocalCacheBestEffort(hash)
    }

    private async reuploadLocalObjectsForVisibility(hashes: Iterable<string>): Promise<number> {
        const entries: Array<{ hash: string; data: Uint8Array }> = []
        for (const hash of new Set(Array.from(hashes).filter(isValidObjectHash))) {
            const data = await this.loadLocalObjectForVisibilityRepair(hash)
            if (data != null) {
                entries.push({ hash, data })
            }
        }

        if (entries.length === 0) {
            return 0
        }

        for (const entry of entries) {
            this.pendingRemoteVisibilityHashes.add(entry.hash)
        }
        try {
            await this.remote.storeBatch(entries)
            return entries.length
        } catch (error) {
            for (const entry of entries) {
                this.pendingRemoteVisibilityHashes.delete(entry.hash)
            }
            throw error
        }
    }

    private async loadLocalCacheBestEffort(hash: string): Promise<Uint8Array | null> {
        let timer: ReturnType<typeof setTimeout> | null = null
        let timedOut = false
        try {
            const localPromise = Promise.resolve(this.local.load(hash)).catch(error => {
                sdkConsole.warn('[sqlite3mt:repo] load:local-error', { hash, error })
                return null
            })
            const timeoutPromise = new Promise<null>(resolve => {
                timer = setTimeout(() => {
                    timedOut = true
                    resolve(null)
                }, LOCAL_CACHE_LOAD_MAX_WAIT_MS)
            })
            const localData = await Promise.race<Uint8Array | null>([localPromise, timeoutPromise])
            if (timedOut) {
                sdkConsole.warn('[sqlite3mt:repo] load:local-timeout', {
                    hash,
                    timeoutMs: LOCAL_CACHE_LOAD_MAX_WAIT_MS,
                })
            }
            return localData
        } finally {
            if (timer != null) {
                clearTimeout(timer)
            }
        }
    }

    /** Add entries to the outbox (used by BlobTransactionImpl). */
    addToMemoryOutbox(blobId: string, entries: Array<{ hash: string; data: Uint8Array }>): void {
        let blobOutbox = this.memoryOutbox.get(blobId)
        if (!blobOutbox) {
            blobOutbox = new Map<string, Uint8Array>()
            this.memoryOutbox.set(blobId, blobOutbox)
        }
        for (const entry of entries) {
            blobOutbox.set(entry.hash, entry.data)
        }
    }

    private forgetRemoteVisibility(): void {
        this.remoteVisibleHashes.clear()
        this.pendingRemoteVisibilityHashes.clear()
        this.remoteMissingHashes.clear()
    }

    /** Check memory outbox for a hash (cross-blob because hashes are content-addressed). */
    private checkMemoryOutbox(hash: string): Uint8Array | null {
        for (const blobOutbox of this.memoryOutbox.values()) {
            const data = blobOutbox.get(hash)
            if (data) {
                return new Uint8Array(data)
            }
        }
        return null
    }

    async load(hash: string): Promise<Uint8Array | null> {
        if (!isValidObjectHash(hash)) {
            return null
        }
        const now = Date.now()
        const existingLoad = this.inflightLoads.get(hash)
        if (existingLoad != null && (now - existingLoad.startedAt) < INFLIGHT_LOAD_STALE_AFTER_MS) {
            sdkConsole.trace('[sqlite3mt:repo] load:join-inflight', { hash })
            return existingLoad.promise
        }
        if (existingLoad != null) {
            sdkConsole.warn('[sqlite3mt:repo] load:stale-inflight', {
                hash,
                ageMs: now - existingLoad.startedAt,
                staleAfterMs: INFLIGHT_LOAD_STALE_AFTER_MS,
            })
        }
        const loadPromise = this.loadDirect(hash)
        this.inflightLoads.set(hash, {
            promise: loadPromise,
            startedAt: now,
        })
        try {
            return await loadPromise
        } finally {
            if (this.inflightLoads.get(hash)?.promise === loadPromise) {
                this.inflightLoads.delete(hash)
            }
        }
    }

    private async loadRemoteObjectBestEffort(opId: number, hash: string): Promise<RemoteObjectLoadResult> {
        let directError: unknown = null

        const loadDirect = async (): Promise<RemoteObjectLoadResult> => {
            sdkConsole.trace('[sqlite3mt:repo] load:remote-fetch', {
                opId,
                hash,
                strategy: 'direct',
            })
            try {
                const remoteData = await this.remote.load(hash)
                if (remoteData != null) {
                    return { data: remoteData, confirmedMissing: false }
                }
                sdkConsole.trace('[sqlite3mt:repo] load:remote-miss', {
                    opId,
                    hash,
                    strategy: 'direct',
                })
                return { data: null, confirmedMissing: true }
            } catch (error) {
                directError = error
                sdkConsole.warn('[sqlite3mt:repo] load:remote-error', {
                    opId,
                    hash,
                    strategy: 'direct',
                    error,
                })
                if (error instanceof EngineException && !error.error.retryable) {
                    const code = error.error.code
                    if (code === 'FORBIDDEN' || code === 'BLOB_SOFT_DELETED') {
                        throw error
                    }
                }
                return { data: null, confirmedMissing: false }
            }
        }

        const loadBatchFallback = async (): Promise<RemoteObjectLoadResult> => {
            // Immutable CAS objects are semantically identical regardless of
            // transport. When the singleton GET path gets wedged under browser
            // contention, retrying through the repository transfer orchestrator is
            // correctness-preserving and lets neighbouring sqlite3mt faults share
            // one POST /objects/batch instead of amplifying into singleton batches.
            sdkConsole.trace('[sqlite3mt:repo] load:remote-fetch', {
                opId,
                hash,
                strategy: 'batch-fallback',
                directError: directError != null,
            })
            try {
                const remoteData = await this.orchestrator.load(hash, 'high')
                sdkConsole.trace('[sqlite3mt:repo] load:remote-miss', {
                    opId,
                    hash,
                    strategy: 'batch-fallback',
                    found: remoteData != null,
                })
                return { data: remoteData, confirmedMissing: remoteData == null }
            } catch (error) {
                sdkConsole.warn('[sqlite3mt:repo] load:remote-error', {
                    opId,
                    hash,
                    strategy: 'batch-fallback',
                    error,
                })
                if (error instanceof EngineException && !error.error.retryable) {
                    const code = error.error.code
                    if (code === 'FORBIDDEN' || code === 'BLOB_SOFT_DELETED') {
                        throw error
                    }
                }
                return { data: null, confirmedMissing: false }
            }
        }

        if (isBrowserLikeObjectLoadRuntime()) {
            // Browser runtimes still keep the singleton batch path as a
            // correctness-preserving fallback, but direct immutable-object GETs
            // are the healthier primary path once zero-length success responses
            // are cleaned up promptly. The batch route has shown longer tail
            // latency during reconcile under 3-way contention.
            const directResult = await loadDirect()
            if (directResult.data != null) {
                return directResult
            }
            const batchResult = await loadBatchFallback()
            if (batchResult.data != null) {
                return batchResult
            }
            return {
                data: null,
                confirmedMissing: directResult.confirmedMissing || batchResult.confirmedMissing,
            }
        }

        const directResult = await loadDirect()
        if (directResult.data != null) {
            return directResult
        }
        const batchResult = await loadBatchFallback()
        if (batchResult.data != null) {
            return batchResult
        }
        return {
            data: null,
            confirmedMissing: directResult.confirmedMissing || batchResult.confirmedMissing,
        }
    }

    private async loadDirect(hash: string): Promise<Uint8Array | null> {
        const opId = ++blobRepositoryOpSeq
        sdkConsole.trace('[sqlite3mt:repo] load:start', { opId, hash })
        // 0. Memory outbox — fastest path for objects written in this runtime
        // but not yet visible through the persistent local store (e.g. IDB
        // transaction still committing or aggressive read timeout).
        const outboxData = this.checkMemoryOutbox(hash)
        if (outboxData) {
            sdkConsole.trace('[sqlite3mt:repo] load:memory-outbox-hit', {
                opId,
                hash,
                bytes: outboxData.byteLength,
            })
            return outboxData
        }

        // 1. Check Local
        const localData = await this.loadLocalCacheBestEffort(hash)
        if (localData) {
            sdkConsole.trace('[sqlite3mt:repo] load:local-hit', {
                opId,
                hash,
                bytes: localData.byteLength,
            })
            return localData
        }
        if (this.isRemoteMissingFresh(hash)) {
            sdkConsole.trace('[sqlite3mt:repo] load:recent-remote-miss', { opId, hash })
            return null
        }

        // 2. Single-object critical-path reads prefer the direct object
        // endpoint, but fall back to the singleton batch path when the direct
        // transport stalls or goes missing under browser contention.
        // Offline-resilient: if remote fetch fails, return null instead of throwing.
        try {
            const remoteResult = await this.loadRemoteObjectBestEffort(opId, hash)
            const remoteData = remoteResult.data

            // 3. Write-Through to Local
            if (remoteData) {
                sdkConsole.trace('[sqlite3mt:repo] load:remote-hit', {
                    opId,
                    hash,
                    bytes: remoteData.byteLength,
                })
                this.markRemoteVisible(hash)
                this.persistLocalCacheBestEffort(hash, remoteData)
                sdkConsole.trace('[sqlite3mt:repo] load:cached-local', { opId, hash })
            }
            sdkConsole.trace('[sqlite3mt:repo] load:done', {
                opId,
                hash,
                found: remoteData != null,
            })
            if (remoteData == null && remoteResult.confirmedMissing) {
                this.markRemoteMissing(hash)
            }
            return remoteData ?? null
        } catch (e) {
            sdkConsole.warn('[sqlite3mt:repo] load:error', { opId, hash, error: e })
            // Only re-throw auth/access errors (FORBIDDEN, BLOB_SOFT_DELETED).
            // Treat other non-retryable errors (e.g. 400 from /objects/batch
            // when server has GC'd old objects) as "data unavailable" (§10.1.1).
            if (e instanceof EngineException && !e.error.retryable) {
                const code = e.error.code
                if (code === 'FORBIDDEN' || code === 'BLOB_SOFT_DELETED') throw e
            }
            // Transient error or data unavailable — object not available
            return null
        }
    }

    async loadBatch(hashes: string[]): Promise<Map<string, Uint8Array>> {
        const normalizedHashes = Array.from(new Set(hashes.filter(isValidObjectHash)))
        const result = new Map<string, Uint8Array>()
        const missing: string[] = []

        // 1. Parallel local check
        await Promise.all(normalizedHashes.map(async hash => {
            const data = await this.local.load(hash)
            if (data) result.set(hash, data)
            else missing.push(hash)
        }))

        if (missing.length === 0) return result

        const remoteFetchHashes = missing.filter(hash => !this.isRemoteMissingFresh(hash))
        if (remoteFetchHashes.length === 0) {
            sdkConsole.trace('[sqlite3mt:repo] loadBatch:recent-remote-misses', {
                count: missing.length,
                hashes: missing,
            })
            return result
        }

        // 2. Batch fetch missing
        // Offline-resilient: if remote fails, return whatever we found locally
        try {
            const remoteResults = await this.orchestrator.loadBatch(remoteFetchHashes)

            // 3. Store new items
            const entriesToStore: Array<{ hash: string; data: Uint8Array }> = []
            for (const [hash, data] of remoteResults) {
                result.set(hash, data)
                this.markRemoteVisible(hash)
                entriesToStore.push({ hash, data })
            }

            if (entriesToStore.length > 0) {
                await this.local.storeBatch(entriesToStore)
            }
            const now = Date.now()
            for (const hash of remoteFetchHashes) {
                if (!remoteResults.has(hash)) {
                    this.markRemoteMissing(hash, now)
                }
            }
        } catch (e) {
            // Only re-throw auth/access errors (FORBIDDEN, BLOB_SOFT_DELETED).
            // Other non-retryable errors treated as "data unavailable" (§10.1.1).
            if (e instanceof EngineException && !e.error.retryable) {
                const code = e.error.code
                if (code === 'FORBIDDEN' || code === 'BLOB_SOFT_DELETED') throw e
            }
            // Transient error or data unavailable — return partial local results
        }

        return result
    }

    async loadBatchStrict(hashes: string[]): Promise<Map<string, Uint8Array>> {
        const normalizedHashes = Array.from(new Set(hashes.filter(isValidObjectHash)))
        const result = new Map<string, Uint8Array>()
        const missing: string[] = []

        await Promise.all(normalizedHashes.map(async hash => {
            const data = await this.local.load(hash)
            if (data) result.set(hash, data)
            else missing.push(hash)
        }))

        const remoteFetchHashes = missing.filter(hash => !this.isRemoteMissingFresh(hash))
        if (remoteFetchHashes.length === 0) {
            return result
        }

        const remoteResults = await this.orchestrator.loadBatch(remoteFetchHashes)
        const entriesToStore: Array<{ hash: string; data: Uint8Array }> = []
        for (const [hash, data] of remoteResults) {
            result.set(hash, data)
            this.markRemoteVisible(hash)
            entriesToStore.push({ hash, data })
        }
        if (entriesToStore.length > 0) {
            await this.local.storeBatch(entriesToStore)
        }
        const now = Date.now()
        for (const hash of remoteFetchHashes) {
            if (!remoteResults.has(hash)) {
                this.markRemoteMissing(hash, now)
            }
        }

        return result
    }

    async hasObjectLocal(hash: string): Promise<boolean> {
        if (!isValidObjectHash(hash)) {
            return false
        }
        if (this.checkMemoryOutbox(hash) != null) {
            return true
        }
        try {
            if (typeof this.local.hasObject === 'function') {
                return await this.local.hasObject(hash)
            }
            return (await this.local.load(hash)) != null
        } catch (error) {
            sdkConsole.warn('[secureblob:repo] hasObjectLocal:error', { hash, error })
            return false
        }
    }

    async findMissingObjects(hashes: Iterable<string>): Promise<string[]> {
        return await this.findMissingRemoteObjects(hashes)
    }

    prefetch(hashes: string[]): void {
        if (!hashes.some(isValidObjectHash)) {
            return
        }
        // Fire and forget loadBatch — catch to prevent unhandled rejection (§3.3 best-effort)
        this.loadBatch(hashes).catch(() => { /* §3.3: prefetch is best-effort */ })
    }

    // ── Transaction ─────────────────────────────────────────────

    transaction(blobId: string): BlobTransaction {
        return new BlobTransactionImpl(blobId, this, this.local, this.remote)
    }

    // ── Sync ────────────────────────────────────────────────────

    async getHeads(blobId: string, lane?: string): Promise<HeadsResult> {
        return this.remote.getHeads(blobId, undefined, lane)
    }

    async updateHead(blobId: string, base: string[], newHead: string, referencedObjects?: string[], prefetchHints?: string[], lane?: string): Promise<HeadUpdateResult> {
        const result = await this.remote.updateHead(blobId, base, newHead, undefined, referencedObjects, prefetchHints, undefined, undefined, lane)
        if (!result.ok && result.code === 'OBJECT_MISSING') {
            this.forgetRemoteVisibility()
        }
        return result
    }

    /**
     * §7.8.4: Upload outbox objects to remote in 50-object chunks.
     * Checks AbortSignal between chunks for pause/cancel support.
     * Returns hashes of successfully uploaded items (may be partial on abort).
     * Does NOT clear the outbox — call confirmPush() after HEAD update succeeds.
     */
    async push(blobId: string, opts?: {
        signal?: AbortSignal
        onProgress?: (uploaded: number, total: number, bytesUploaded: number, bytesTotal: number) => void
    }): Promise<string[]> {
        // 1. Load from persistent outbox if available
        let pending: Array<{ hash: string; data: Uint8Array }> = []
        if (this.local.loadOutbox) {
            pending = await this.local.loadOutbox(blobId)
        } else {
            const mem = this.memoryOutbox.get(blobId)
            if (mem) pending = Array.from(mem.entries()).map(([hash, data]) => ({ hash, data }))
        }

        if (pending.length === 0) return []

        // Filter out objects already uploaded in a previous push attempt
        // (e.g., after CAS_MISMATCH where objects are on server but outbox wasn't cleared yet)
        const alreadyUploaded = this.uploadedHashes.get(blobId)
        if (alreadyUploaded?.size) {
            pending = pending.filter(e => !alreadyUploaded.has(e.hash))
        }
        if (pending.length === 0) return alreadyUploaded ? [...alreadyUploaded] : []

        const CHUNK_SIZE = 50
        const totalBytes = pending.reduce((sum, e) => sum + e.data.byteLength, 0)
        const uploaded: string[] = []
        let bytesUploaded = 0

        // §7.8.4: Chunk ourselves (not storeBatch) to insert abort checkpoints
        for (let i = 0; i < pending.length; i += CHUNK_SIZE) {
            // E1: Abort checkpoint between chunks
            if (opts?.signal?.aborted) break

            const chunk = pending.slice(i, i + CHUNK_SIZE)

            try {
                // Try batch-put for this chunk
                await this.remote.storeBatch(chunk)
                for (const e of chunk) {
                    uploaded.push(e.hash)
                    bytesUploaded += e.data.byteLength
                    // Track successful upload for dedup on retry
                    if (!this.uploadedHashes.has(blobId)) this.uploadedHashes.set(blobId, new Set())
                    this.uploadedHashes.get(blobId)!.add(e.hash)
                }
            } catch {
                // E2: Fallback to individual PUTs — respect abort between each
                for (const entry of chunk) {
                    if (opts?.signal?.aborted) break
                    try {
                        await this.remote.store(entry.hash, entry.data)
                        uploaded.push(entry.hash)
                        bytesUploaded += entry.data.byteLength
                        if (!this.uploadedHashes.has(blobId)) this.uploadedHashes.set(blobId, new Set())
                        this.uploadedHashes.get(blobId)!.add(entry.hash)
                    } catch (putErr) {
                        sdkConsole.warn(`[BlobRepository] Push failed for object ${entry.hash}:`, putErr)
                        if (putErr instanceof EngineException) throw putErr
                        if (opts?.signal?.aborted) break
                        throw putErr
                    }
                }
            }

            // Progress callback after each chunk
            opts?.onProgress?.(uploaded.length, pending.length, bytesUploaded, totalBytes)
        }

        return alreadyUploaded?.size
            ? [...new Set([...alreadyUploaded, ...uploaded])]
            : uploaded
    }

    /**
     * Clear outbox entries after objects are safely uploaded and the current
     * publish path has proved remote visibility for them. Objects are content-
     * addressed and immutable, but upload ACKs are only response-time facts; the
     * caller must run ensureRemoteVisible() before clearing and before HEAD CAS.
     */
    async confirmPush(blobId: string, hashes: string[]): Promise<void> {
        if (hashes.length === 0) return
        if (this.local.clearOutbox) {
            await this.local.clearOutbox(blobId, hashes)
        }
        // Always clear memory outbox — it is kept in sync regardless of
        // whether the persistent backend has a separate outbox store.
        const mem = this.memoryOutbox.get(blobId)
        if (mem) hashes.forEach(h => mem.delete(h))
        // Clear upload tracking — full cycle complete
        this.uploadedHashes.delete(blobId)
    }

    async discardOutbox(blobId: string): Promise<number> {
        const hashes = new Set<string>()
        if (this.local.loadOutbox) {
            for (const entry of await this.local.loadOutbox(blobId)) {
                hashes.add(entry.hash)
            }
        }

        const mem = this.memoryOutbox.get(blobId)
        if (mem) {
            for (const hash of mem.keys()) {
                hashes.add(hash)
            }
        }

        if (hashes.size > 0 && this.local.clearOutbox) {
            await this.local.clearOutbox(blobId, [...hashes])
        }

        mem?.clear()
        this.uploadedHashes.delete(blobId)
        return hashes.size
    }

    /** R2: Bulk push pre-collected objects directly to remote (for recovery) */
    async pushObjects(objects: Array<{ hash: string; data: Uint8Array }>, opts?: { force?: boolean }): Promise<void> {
        if (objects.length === 0) return
        const opId = ++blobRepositoryOpSeq
        const now = Date.now()
        const force = opts?.force === true
        const toUpload = force
            ? objects
            : objects.filter(object =>
                !this.isRemoteVisibilityFresh(object.hash, now)
                && !this.pendingRemoteVisibilityHashes.has(object.hash)
            )
        sdkConsole.trace('[sqlite3mt:repo] pushObjects:start', {
            opId,
            count: objects.length,
            uploadCount: toUpload.length,
            force,
            hashes: objects.map(object => object.hash),
        })

        // Keep uploads strict and deterministic. Per-object fallback here can
        // mask the actual transport/liveness regression we need sqlite3mt to
        // surface and fix.
        try {
            if (toUpload.length > 0) {
                for (const object of toUpload) {
                    this.pendingRemoteVisibilityHashes.add(object.hash)
                }
                await this.remote.storeBatch(toUpload)
            }
            this.persistLocalBatchBestEffort(objects)
            sdkConsole.trace('[sqlite3mt:repo] pushObjects:done', {
                opId,
                count: objects.length,
                uploadCount: toUpload.length,
            })
        } catch (error) {
            for (const object of toUpload) {
                this.pendingRemoteVisibilityHashes.delete(object.hash)
            }
            sdkConsole.warn('[sqlite3mt:repo] pushObjects:error', { opId, error })
            throw error
        }
    }

    async ensureRemoteVisible(hashes: Iterable<string>): Promise<void> {
        const pending = new Set(Array.from(hashes).filter(isValidObjectHash))
        if (pending.size === 0) {
            return
        }

        const maxAttempts = 5
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            sdkConsole.trace('[sqlite3mt:repo] ensureRemoteVisible:attempt', {
                attempt,
                pending: Array.from(pending),
            })
            const missing = new Set(await this.findMissingRemoteObjects(pending))
            for (const hash of Array.from(pending)) {
                if (!missing.has(hash)) {
                    this.markRemoteVisible(hash)
                    pending.delete(hash)
                }
            }

            if (pending.size === 0) {
                sdkConsole.trace('[sqlite3mt:repo] ensureRemoteVisible:done', {
                    attempt,
                })
                return
            }

            if (attempt < maxAttempts) {
                const repairedCount = await this.reuploadLocalObjectsForVisibility(pending)
                if (repairedCount > 0) {
                    sdkConsole.trace('[sqlite3mt:repo] ensureRemoteVisible:reuploaded-local', {
                        attempt,
                        repairedCount,
                        pending: Array.from(pending),
                    })
                }
                await delay(100 * attempt)
            }
        }

        sdkConsole.error('[sqlite3mt:repo] ensureRemoteVisible:failed', {
            pending: Array.from(pending),
        })
        const now = Date.now()
        for (const hash of pending) {
            this.remoteVisibleHashes.delete(hash)
            this.pendingRemoteVisibilityHashes.delete(hash)
            this.markRemoteMissing(hash, now)
        }
        throw new Error(`Remote visibility barrier failed for ${pending.size} object(s): ${Array.from(pending).join(', ')}`)
    }

    // Persistence & Head Management
    async getLocalHeads(blobId: string): Promise<string[]> {
        return (await this.local.getLocalHeads?.(blobId)) ?? []
    }

    async setLocalHeads(blobId: string, heads: string[]): Promise<void> {
        await this.local.setLocalHeads?.(blobId, heads)
    }

    async getRemoteHead(blobId: string): Promise<string | undefined> {
        return await this.local.getRemoteHead?.(blobId)
    }

    async setRemoteHead(blobId: string, head: string): Promise<void> {
        await this.local.setRemoteHead?.(blobId, head)
    }

    async setHeadsAtomically(blobId: string, opts: { local?: string[]; remote?: string }): Promise<void> {
        if (this.local.setHeadsAtomically) {
            await this.local.setHeadsAtomically(blobId, opts)
        } else {
            // Fallback for MemoryStoreBackend
            if (opts.local !== undefined) await this.local.setLocalHeads?.(blobId, opts.local)
            if (opts.remote !== undefined) await this.local.setRemoteHead?.(blobId, opts.remote)
        }
    }

    async softDeleteBlob(blobId: string): Promise<void> {
        await this.remote.softDeleteBlob?.(blobId)
    }

    // ── Lease API (§94 §7) ─────────────────────────────────────

    async acquireLease(blobId: string, resourceId: string, mode: 0 | 1 | 2, writerKid: string, ttlSeconds = 30): Promise<LeaseResult | null> {
        if (!this.remote.acquireLease) return null
        const result = await this.remote.acquireLease(blobId, resourceId, mode, writerKid, ttlSeconds)
        if (!result) return null
        return { leaseId: result.leaseId, resourceId: result.resourceId ?? resourceId, mode: result.mode, expiresAt: result.expiresAt }
    }

    async refreshLease(blobId: string, leaseId: string, ttlSeconds = 30): Promise<LeaseResult | null> {
        if (!this.remote.refreshLease) return null
        const result = await this.remote.refreshLease(blobId, leaseId, ttlSeconds)
        if (!result) return null
        return { leaseId: result.leaseId, resourceId: result.resourceId ?? '', mode: (result.mode ?? 0) as 0 | 1 | 2, expiresAt: result.expiresAt }
    }

    async releaseLease(blobId: string, leaseId: string): Promise<boolean> {
        if (!this.remote.releaseLease) return false
        return this.remote.releaseLease(blobId, leaseId)
    }

    async updateHeadWithLease(blobId: string, base: string[], newHead: string, opts?: {
        leaseId?: string
        releaseLeaseId?: string
        referencedObjects?: string[]
        prefetchHints?: string[]
    }): Promise<HeadUpdateResult> {
        const result = await this.remote.updateHead(blobId, base, newHead, undefined, opts?.referencedObjects, opts?.prefetchHints, opts?.leaseId, opts?.releaseLeaseId)
        if (!result.ok && result.code === 'OBJECT_MISSING') {
            this.forgetRemoteVisibility()
        }
        return result
    }
}

class BlobTransactionImpl implements BlobTransaction {
    private staged = new Map<string, Uint8Array>()

    constructor(
        private blobId: string,
        private repo: BlobRepositoryImpl,
        private local: StoreBackend,
        private remote: StoreBackend
    ) { }

    put(hash: string, data: Uint8Array): void {
        this.staged.set(hash, data)
    }

    async commit(headUpdate?: { base: string[]; new: string; referencedObjects?: string[] }): Promise<HeadUpdateResult> {
        // 1. Persist to Local
        const entries = Array.from(this.staged.entries()).map(([hash, data]) => ({ hash, data }))
        if (entries.length > 0) {
            await this.local.storeBatch(entries)
        }

        // 2. Add to Outbox for background sync
        if (entries.length > 0) {
            if (this.local.storeOutbox) {
                for (const entry of entries) {
                    await this.local.storeOutbox(this.blobId, entry.hash, entry.data)
                }
            }
            // Always keep a memory copy so immediate reads (e.g. commit pipeline
            // building the next tree or sqlite3mt fault handler) find the object
            // without waiting for IDB commit latency or hitting remote 404s.
            this.repo.addToMemoryOutbox(this.blobId, entries)
        }

        // 3. Update Head (Optional — push + CAS)
        // §5.1.1/§7.2: When headUpdate is provided (e.g. tombstone commit),
        // push all outbox objects to remote before the CAS HEAD update.
        // Without headUpdate, objects accumulate in the outbox and syncNow()
        // handles the batched upload in efficient 50-object chunks (§7.2 step 3).
        if (headUpdate) {
            let pushed: string[] = []
            try {
                pushed = await this.repo.push(this.blobId)
                if (pushed.length > 0) {
                    await this.repo.ensureRemoteVisible(pushed)
                }
                // The new commit object is the CAS prerequisite. referencedObjects
                // remains attached to updateHead as server-GC metadata, but must
                // not expand this publish barrier to the whole live workspace set.
                await this.repo.ensureRemoteVisible([headUpdate.new])
            } catch (error) {
                // If this is an EngineException, propagate it directly so details reach the UI
                if (error instanceof EngineException) throw error
                return {
                    ok: false,
                    code: 'NETWORK_ERROR',
                    message: error instanceof Error ? error.message : 'Push failed',
                }
            }
            const result = await this.repo.updateHead(this.blobId, headUpdate.base, headUpdate.new, headUpdate.referencedObjects)
            if (result.ok) {
                this.staged.clear()
                await this.repo.confirmPush(this.blobId, pushed)
            }
            return result
        }

        // No head update — persist locally + outbox only.
        // syncNow() will batch-upload all outbox objects in 50-object chunks (§7.2 step 3).
        this.staged.clear()
        return { ok: true }
    }
}
